import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Users, Grid, Shield, Zap, Globe, Star } from 'lucide-react';

const Landing = () => {
  const plans = [
    {
      name: "Free Forever",
      price: "$0",
      period: "forever",
      description: "Perfect for personal use and testing",
      features: [
        "20 users",
        "6 tiles",
        "0 groups",
        "1 admin",
        "100 MB storage",
        "Powered by Taptile footer",
        "Community support"
      ],
      popular: false,
      cta: "Get Started Free"
    },
    {
      name: "Basic",
      price: "$39",
      period: "per month",
      description: "Great for small teams and departments",
      features: [
        "500 users",
        "20 tiles",
        "10 groups",
        "1 admin",
        "1 GB storage",
        "Remove branding",
        "CSV import",
        "Push notifications",
        "Email support (48h)"
      ],
      popular: false,
      cta: "Start Basic"
    },
    {
      name: "Pro",
      price: "$99",
      period: "per month",
      description: "For medium-large organizations",
      features: [
        "2,500 users",
        "Unlimited tiles & groups",
        "2 admins",
        "10 GB storage",
        "Scheduled notifications",
        "Analytics dashboard",
        "Wonde + SSO integration",
        "Custom CSS",
        "2FA",
        "Priority support (24h)"
      ],
      popular: true,
      cta: "Start Pro"
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large schools, universities, corporations",
      features: [
        "Unlimited everything",
        "Multiple portals",
        "Custom domain",
        "SAML SSO + LDAP",
        "Multi-language",
        "99.9% SLA",
        "24/7 support",
        "Dedicated manager"
      ],
      popular: false,
      cta: "Contact Sales"
    }
  ];

  const features = [
    {
      icon: <Users className="h-8 w-8 text-blue-600" />,
      title: "User Management",
      description: "Easily manage users, groups, and permissions with our intuitive admin dashboard."
    },
    {
      icon: <Grid className="h-8 w-8 text-green-600" />,
      title: "App Tiles",
      description: "Create beautiful, customizable tiles for all your organization's apps and links."
    },
    {
      icon: <Shield className="h-8 w-8 text-purple-600" />,
      title: "Secure Access",
      description: "Control who sees what with group-based permissions and secure authentication."
    },
    {
      icon: <Zap className="h-8 w-8 text-yellow-600" />,
      title: "Lightning Fast",
      description: "Built for performance with instant loading and responsive design."
    },
    {
      icon: <Globe className="h-8 w-8 text-indigo-600" />,
      title: "PWA Ready",
      description: "Install as a Progressive Web App for native-like experience on any device."
    },
    {
      icon: <Star className="h-8 w-8 text-red-600" />,
      title: "Customizable",
      description: "Brand your portal with custom colors, logos, and styling to match your organization."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 relative">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center py-4 px-4 sm:px-6 lg:px-8">
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
              <img 
                src="/logo.svg" 
                alt="Taptile Logo" 
                className="h-8 w-8 sm:h-10 sm:w-10"
              />
              <span className="text-xl sm:text-2xl font-bold text-gray-900 font-heading">
                Taptile
              </span>
            </Link>
            <div className="flex items-center space-x-1 sm:space-x-4">
              {/* Hide secondary links on mobile */}
              <Link to="/help" className="hidden sm:block">
                <Button variant="ghost">Help Center</Button>
              </Link>
              <Link to="/contact" className="hidden sm:block">
                <Button variant="ghost">Contact</Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="sm:text-base sm:px-4">Sign In</Button>
              </Link>
              <Link to="/login">
                <Button size="sm" className="sm:text-base sm:px-4">Start for Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-slate-900 tracking-tight">
              One tap.<br />Everything.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mt-4">
              Your brand, your tiles, your app.
            </p>
            <p className="text-base text-gray-600 mt-6 max-w-xl mx-auto lg:mx-0">
              Create a beautiful, secure portal for your team to access all their apps, tools and resources in one place.
              Perfect for schools, businesses and organisations of any size.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mt-8">
              <Link to="/login">
                <Button size="lg" className="text-lg px-8 py-3 w-full sm:w-auto" data-testid="hero-start-free">
                  Start for Free
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="text-lg px-8 py-3 w-full sm:w-auto" data-testid="hero-contact-sales">
                  Contact Sales
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              No credit card required • Get started in minutes
            </p>
          </div>
          <div className="relative">
            <img
              src="/marketing/hero-phones.png"
              alt="The Taptile portal shown on three phones, branded for a school, a business and a community group"
              className="w-full h-auto"
              width="2234"
              height="1598"
              loading="eager"
              data-testid="hero-phones-image"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything you need to manage your digital workspace
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features designed to make it easy for your team to find and access the tools they need.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="mb-4">{feature.icon}</div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-brand banner */}
      <section className="bg-white" data-testid="multi-brand-band">
        <img
          src="/marketing/taptile-banner-your-brand.jpg"
          alt="One platform, your brand — the same Taptile portal branded for a school, a business and a community group"
          className="w-full h-auto"
          width="1920"
          height="600"
          loading="lazy"
        />
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that&apos;s right for your organization. All plans include our core features.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan, index) => (
              <Card key={index} className={`relative ${plan.popular ? 'border-blue-500 shadow-xl scale-105' : 'border-gray-200'}`}>
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500">
                    Most Popular
                  </Badge>
                )}
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.period && <span className="text-gray-600 ml-2">/{plan.period}</span>}
                  </div>
                  <CardDescription className="mt-2">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full" 
                    variant={plan.popular ? "default" : "outline"}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-8">
            Ready to transform your digital workspace?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-3">
                Start for Free
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="text-lg px-8 py-3 text-white border-white hover:bg-white hover:text-blue-600">
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="/logo.svg" 
                  alt="Taptile Logo" 
                  className="h-8 w-8"
                />
                <span className="text-xl font-bold">Taptile</span>
              </div>
              <p className="text-gray-400">
                The modern way to organize and access your organization&apos;s digital tools.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
                <li><a href="#" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/support" className="hover:text-white">Support &amp; FAQs</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
                <li><a href="mailto:taptile@dynatexsaas.com" className="hover:text-white">Email Support</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Taptile by Dynatex SaaS Co., Th. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;